import { Inject, Injectable } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, finalize, map, mergeMap, take } from 'rxjs/operators';
import { BarcodeScannerModalComponent } from '../../components/barcode-scanner-modal/barcode-scanner-modal.component';
import { ConfigConstants } from '../../config/config-constants';
import { BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { PaceBarcode } from '@evorait/pace-components';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import {
    BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY,
    BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY,
    BARCODE_CLIPBOARD_MAX_QUEUE,
    BarcodeScanResult,
} from './barcode-scanner.const';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable()
export class BarcodeScannerService implements BarcodeScannerInterface {
    private barcodeClipboard: BehaviorSubject<PaceBarcode[]> = new BehaviorSubject([]);
    public barcodeClipboard$: Observable<PaceBarcode[]> = this.barcodeClipboard.asObservable();
    public constructor(
        private readonly modalService: BsModalService,
        private readonly localStorageCommonService: LocalStorageCommonService,
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.updateClipboard();
        window.addEventListener('storage', (event) => {
            if (event.key === BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY) {
                this.updateClipboard();
            }
        });
    }

    private updateClipboard(): void {
        this.localStorageCommonService
            .storageGetAsync(BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY)
            .then((storedValue: PaceBarcode[]) => {
                if (storedValue?.length) {
                    this.barcodeClipboard.next(storedValue);
                } else {
                    this.barcodeClipboard.next([]);
                }
            })
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to update the barcode clipboard', error);
            });
    }

    public scan(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): Observable<BarcodeScanResult> {
        const modalInstance = this.modalService.show(BarcodeScannerModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { pattern, appId, clipboardAccessible },
            ariaLabelledBy: 'barcode-scanner-modal-title',
        });

        return modalInstance.content?.scanCompleted.asObservable() ?? of({ text: '', format: '' });
    }

    public setBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean> {
        if (!barcodes?.length) {
            barcodes = [];
        }
        if (barcodes.length > BARCODE_CLIPBOARD_MAX_QUEUE) {
            barcodes = barcodes.slice(0, BARCODE_CLIPBOARD_MAX_QUEUE);
        }

        return from(this.localStorageCommonService.storageSetAsync(BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY, barcodes)).pipe(
            mergeMap(() => this.uploadBarcodeClipboard(barcodes)),
            catchError((error) => {
                this.log.error(this.constructor.name, 'failed to store barcode clipboard', error);
                return of(false);
            }),
            finalize(() => this.updateClipboard()),
        );
    }

    public uploadBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean> {
        const newValue = JSON.stringify(barcodes);
        return this.concurrentUsageService
            .writeDataForKey(BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY, newValue, Date.now())
            .pipe(map((data) => data.key === BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY && data.value === newValue));
    }

    public fetchBarcodeClipboard(): Observable<boolean> {
        return this.concurrentUsageService.readDataForKey(BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY).pipe(
            take(1),
            mergeMap((data) => {
                if (data?.length < 1) {
                    return this.setBarcodeClipboard([]);
                }
                try {
                    const barcodeClipboard = JSON.parse(data[0].value) as PaceBarcode[];
                    return this.setBarcodeClipboard(barcodeClipboard);
                } catch (e) {
                    this.log.error(
                        `${this.constructor.name}: was not able to parse barcode clipboard data: ${JSON.stringify(
                            data,
                        )} -- error: ${JSON.stringify(e)}`,
                    );
                    return this.setBarcodeClipboard([]);
                }
            }),
        );
    }
}
